import {useEffect, useState} from "react";
import * as React from "react";
import {ExtratoColaborador, IExtrato} from "../api/ExtratoColaborador";
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    TextField,
    Box,
    MenuItem,
    TableFooter,
    TablePagination
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import {ICredencial} from "../api/ApiCredencial";
import useTheme from "@mui/material/styles/useTheme";
import IconButton from "@mui/material/IconButton";
import {KeyboardArrowLeft, KeyboardArrowRight, FirstPage, LastPage } from "@mui/icons-material";


function formatDateToDDMMYYYY(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

function getStatusColor(status: any) {
    switch (status) {
        case 'PENDENTE':
            return 'grey';
        case 'ATRASADA':
            return 'orange';
        case 'PAGA':
            return 'green';
        case 'CANCELADA':
            return 'red';
        default:
            return 'green'; // Cor padrão caso o status não seja reconhecido
    }
}

function getCategoria(categoria: any) {
    switch (categoria) {
        case 'ATIVIDADE':
            return 'Atividades';
        case 'ODONTOLOGIA_LOJA':
            return 'Clínica Odontológica/Loja';
        case 'ALIMENTACAO':
            return 'Alimentação';
        case 'CLINICA_SESC':
            return 'Clínica Sesc';
        default:
            return 'Sem Categoria'; // Cor padrão caso o status não seja reconhecido
    }
}

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
            </IconButton>
        </Box>
    );
}


function ExtratoTable({credencial}: { credencial: ICredencial }) {
    const mat = credencial.matricula.replace(/-/g, '');
    const cpf = credencial.nucpf;
    const categoria_servidor = credencial.categoria;
    
    const [dados, setDados] = useState<Array<IExtrato>>([]);
    // Ordenar o array de dados por data antes de renderizar
    // @ts-ignore
    dados.sort((a, b) => b.data - a.data);

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);
    const [dataInicio, setDataInicio] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [dataFim, setDataFim] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]);
    const [filtroCategoria, setFiltroCategoria] = useState("TODOS"); // Estado para controlar a opção selecionada
    const [dadosFiltrados, setDadosFiltrados] = useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);


    const categories = [
        {value: "TODOS", label: "Todos"},
        {value: "ODONTOLOGIA_LOJA", label: "Clínica Odontológica/Loja"},
        {value: "ATIVIDADE", label: "Atividades"},
        {value: "ALIMENTACAO", label: "Alimentação"},
        {value: "CLINICA_SESC", label: "Clínica Sesc"},
    ]

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dadosFiltrados.length) : 0;

    const fetchData = async () => {
        try {
            const response = await ExtratoColaborador.getExtratoGeral(mat, dataInicio, dataFim);
            const datacv = await response;

            const responsegm = await ExtratoColaborador.getExtratoGm(cpf, dataInicio, dataFim);
            const datagm = await responsegm;

            const data = [...datacv, ...datagm];

            // @ts-ignore
            setDados(data);
            // @ts-ignore
            setDadosFiltrados(data);
            setCarregando(false);
        } catch (error) {
            setErro(true);
            setCarregando(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [credencial, dataInicio, dataFim]);

    const handleDataInicioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDataInicio(event.target.value);
    };

    const handleDataFimChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDataFim(event.target.value);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        fetchData();
    };

    const handleFiltroCategoriaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFiltroCategoria(event.target.value);
        filtrarDados(event.target.value); // Chama a função de filtro ao selecionar uma opção
        handleChangePage(null, 0); // Redefine a página para 0 quando a categoria é alterada
    };

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filtrarDados = (categoria: string) => {
        if (categoria === "TODOS") {
            setDadosFiltrados(dados); // Exibe todos os dados
        } else {
            const dadosFiltrados = dados.filter(item => item.categoria === categoria);
            setDadosFiltrados(dadosFiltrados);
        }
    };


    const categoriasIgnoradas = ['SERVIDOR', 'SENAC', 'FECOMERCIO'];

    console.log("categoriasIgnoradas extrato: ", categoriasIgnoradas);

    console.log("categoria_servidor.nome extrato: ", categoria_servidor.nome);

    if (!categoriasIgnoradas.includes(categoria_servidor.nome)) {
        return null; // Retorna null se a categoria não for uma das categoriasIgnoradas
    }

    // Criar um objeto para armazenar os totais por categoria
    const categoryTotals = {};
    const totalGeral = dadosFiltrados.reduce((acc, item) => acc + item.valor, 0);

    // Calcular os totais por categoria
    dadosFiltrados.forEach((item) => {
        const categoria = item.categoria;
        if (!categoryTotals[categoria]) {
            categoryTotals[categoria] = 0;
        }
        categoryTotals[categoria] += item.valor;
    });


    // @ts-ignore
    return (
        <>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Extrato</Typography>
                </AccordionSummary>
                <AccordionDetails>
                        <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center" gap={2}>
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    id="outlined-select-currency"
                                    select
                                    label="Categoria"
                                    defaultValue="TODOS"
                                    onChange={handleFiltroCategoriaChange}
                                >
                                    {categories.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Data Início"
                                    type="date"
                                    value={dataInicio}
                                    onChange={handleDataInicioChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                                <TextField
                                    label="Data Fim"
                                    type="date"
                                    value={dataFim}
                                    onChange={handleDataFimChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </form>
                            {carregando ? (
                                <p>Carregando...</p>
                            ) : erro ? (
                                <h1>Desculpe, não foi possivel exibir seu extrato!</h1>
                            ) : dadosFiltrados.length === 0 ? (
                                <TableContainer component={Paper}>
                                    <Table sx={{ minWidth: '100%', width: 500 }} aria-label="custom pagination table">
                                        <TableHead>
                                            <TableRow style={{backgroundColor: '#004c99'}}>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Data</TableCell>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Descrição</TableCell>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Categoria</TableCell>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Valor</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    Nenhum resultado encontrado
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table sx={{ minWidth: '100%', width: 500 }} aria-label="custom pagination table">
                                        <TableHead>
                                            <TableRow style={{backgroundColor: '#004c99'}}>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Data</TableCell>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Descrição</TableCell>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Categoria</TableCell>
                                                <TableCell style={{fontWeight: 'bold', color: 'white'}}>Valor</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(rowsPerPage > 0
                                                    ? dadosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    : dadosFiltrados
                                            ).map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell style={{fontWeight: 'bold'}}>{formatDateToDDMMYYYY(new Date(item.data))}</TableCell>
                                                    <TableCell>{item.descricao}</TableCell>
                                                    <TableCell>{getCategoria(item.categoria)}</TableCell>
                                                    <TableCell>{item.valor.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})}</TableCell>
                                                </TableRow>
                                            ))}
                                            {emptyRows > 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6}/>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                        <TableFooter>
                                            <TableRow>
                                                <TableCell colSpan={3} align="right">
                                                    <b>Total</b>
                                                </TableCell>
                                                <TableCell>
                                                    {/* Exibir o total por categoria selecionada */}
                                                    {filtroCategoria !== 'TODOS'
                                                        ? categoryTotals[filtroCategoria]?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || ''
                                                        : totalGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                </TableCell>
                                                <TableCell colSpan={2} />
                                            </TableRow>
                                            <TableRow>
                                                <TablePagination
                                                    rowsPerPageOptions={[5, 10, 25, { label: 'Todos', value: -1 }]}
                                                    colSpan={5}
                                                    count={dadosFiltrados.length}
                                                    rowsPerPage={rowsPerPage}
                                                    page={page}
                                                    SelectProps={{
                                                        inputProps: {
                                                            'aria-label': 'rows per page',
                                                        },
                                                        native: true,
                                                    }}
                                                    onPageChange={handleChangePage}
                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                    ActionsComponent={TablePaginationActions}
                                                    labelRowsPerPage="Linhas por página"
                                                    labelDisplayedRows={({ from, to, count }) =>
                                                        `${from}-${to} de ${count !== -1 ? count : 'mais de ' + to}`
                                                    }
                                                />
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                </AccordionDetails>
            </Accordion>
        </>
    );
}

export default ExtratoTable;

